const filterMilestoneByPlanningSummarySimplified = (milestone, currentFilter, initiativesArray) => {
  const milestoneStatus = milestone.status;

  // Extract actual dates from history
  let actualStartDate = null;
  let actualEndDate = null;

  // Ensure initiativesArray is an array
  const initiatives = Array.isArray(initiativesArray) ? initiativesArray : [initiativesArray].filter(Boolean);

  // Find the initiative that contains this milestone's history
  const initiative = initiatives?.find(init =>
    init?.history?.some(h => h.number === milestone.milestoneNumber)
  );

  if (initiative?.history) {
    const history = initiative.history;
    const mId = milestone.milestoneNumber;

    // Find actual start date (status changed from 'Not Started' to 'Started')
    const actualStartEntry = history
      .filter(
        h =>
          h.number === mId &&
          h.changes?.status?.previous === 'Not Started' &&
          h.changes?.status?.new === 'Started'
      )
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];

    // Find actual completion date (status changed from 'Started' to 'Completed')
    const actualCompletedEntry = history
      .filter(
        h =>
          h.number === mId &&
          h.changes?.status?.previous === 'Started' &&
          h.changes?.status?.new === 'Completed'
      )
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];

    actualStartDate = actualStartEntry ? new Date(actualStartEntry.timestamp) : null;
    actualEndDate = actualCompletedEntry ? new Date(actualCompletedEntry.timestamp) : null;
  }

  // Handle terminal states
  if (milestoneStatus === 'Cancelled') {
    return currentFilter.includes('Cancelled');
  }

  // Handle missing dates
  const milestoneStartDate = milestone.startDate ? new Date(milestone.startDate) : null;
  const milestoneEndDate = milestone.endDate ? new Date(milestone.endDate) : null;

  if (!milestoneStartDate || !milestoneEndDate) {
    return currentFilter.includes('<not set>');
  }

  // Helper function to get date at UTC midnight
  const getUTCMidnight = (date) => {
    if (!date) return null;
    const dateObj = date instanceof Date ? date : new Date(date);
    return new Date(Date.UTC(
      dateObj.getUTCFullYear(),
      dateObj.getUTCMonth(),
      dateObj.getUTCDate(),
      0, 0, 0, 0
    ));
  };
  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.getUTCFullYear() === date2.getUTCFullYear() &&
      date1.getUTCMonth() === date2.getUTCMonth() &&
      date1.getUTCDate() === date2.getUTCDate();
  };
  const startOfDay = (date) => {
    if (!date) return null;
    const dateObj = date instanceof Date ? date : new Date(date);
    return new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
  };

  // Normalize all dates to UTC midnight
  const currentDate = startOfDay(new Date());
  const normalizedStartDate = startOfDay(milestoneStartDate);
  const normalizedEndDate = startOfDay(milestoneEndDate);
  const normalizedActualStartDate = actualStartDate ? startOfDay(actualStartDate) : null;
  const normalizedActualEndDate = actualEndDate ? startOfDay(actualEndDate) : null;

  const msInDay = 24 * 60 * 60 * 1000;

  // Calculate days differences using Math.ceil for future dates
  const daysUntilEnd = normalizedEndDate >= currentDate
    ? Math.ceil((normalizedEndDate.getTime() - currentDate.getTime()) / msInDay)
    : null;
  const daysUntilStart = normalizedStartDate >= currentDate
    ? Math.ceil((normalizedStartDate.getTime() - currentDate.getTime()) / msInDay)
    : null;
  const daysSinceStart = currentDate > normalizedStartDate
    ? Math.floor((currentDate.getTime() - normalizedStartDate.getTime()) / msInDay)
    : null;

  // Helper functions
  const isLatelyStarted = () => {
    return milestoneStatus === 'Started' &&
      normalizedActualStartDate &&
      normalizedStartDate &&
      normalizedActualStartDate > normalizedStartDate;
  };

  const isLatelyCompleted = () => {
    return milestoneStatus === 'Completed' &&
      normalizedActualEndDate &&
      normalizedEndDate &&
      normalizedActualEndDate > normalizedEndDate;
  };


  // --- HANDLE COMPLETED MILESTONES ---
  if (milestoneStatus === 'Completed') {
    // Lately Completed
    if (isLatelyCompleted()) {
      return currentFilter.includes('Lately Completed');
    }
    // Lately Started but Completed on Time
    if (isLatelyStarted()) {
      return currentFilter.includes('Lately Started');
    }
    // Regular Completed
    return currentFilter.includes('Completed');
  }

  // --- HANDLE NOT STARTED MILESTONES ---
  if (milestoneStatus === 'Not Started') {
    // Priority 1: Late to Finish (end date passed)
    if (normalizedEndDate && currentDate > normalizedEndDate) {
      return currentFilter.includes('Late');
    }

    // Priority 2: Late to Start (start date has passed)
    if (normalizedStartDate && currentDate > normalizedStartDate &&
      normalizedEndDate && currentDate < normalizedEndDate) {
      return currentFilter.includes('Late to start');
    }

    // Priority 3: Due Soon based on start date (within 30 days)
    if (daysUntilStart !== null && daysUntilStart <= 30) {
      if (daysUntilStart <= 7) {
        return currentFilter.includes('Due soon');
      }
      return currentFilter.includes('Due soon for 30 days');
    }

    // Priority 4: On Plan (start date > 30 days away)
    if (daysUntilStart !== null && daysUntilStart > 30) {
      return currentFilter.includes('On plan');
    }

    return currentFilter.includes('Not started');
  }

  // --- HANDLE STARTED MILESTONES ---
  if (milestoneStatus === 'Started') {
    // Priority 1: Late to Finish (end date passed)
    if (normalizedEndDate && currentDate > normalizedEndDate) {
      return currentFilter.includes('Late');
    }

    // Priority 2: Due Soon based on end date (within 30 days)
    if (daysUntilEnd !== null && daysUntilEnd <= 30) {
      if (daysUntilEnd <= 7) {
        return currentFilter.includes('Due soon');
      }
      return currentFilter.includes('Due soon for 30 days');
    }

    // Priority 3: Lately Started (end date > 30 days away AND started late)
    if (isLatelyStarted()) {
      return currentFilter.includes('Lately Started');
    }

    // Priority 4: On Plan (end date > 30 days away AND started on time)
    return currentFilter.includes('On plan');
  }

  // Handle Not planned
  if (milestoneStatus === 'Not planned') {
    return currentFilter.includes('Not planned');
  }

  return false;
};

export default filterMilestoneByPlanningSummarySimplified;